<template>
  <ModalDialog
    ref="dialog"
    title="Add Adjustment"
    :submitting="submitting"
    :submitDisabled="submitDisabled"
    submitTitle="Submit"
    icon="fa-exchange"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3">
      <FormRow label="Stock">
        <FormSearchableSelect :options="stockList" v-model="transaction.stockId" placeholder="Stock Code" field="stockId" :dropdownAutoWidth="true" :loading="false" />
      </FormRow>
      <FormRow label="Reason">
        <FormSelect placeholder="Enter Reason" :optionsPromise="adjustReasonListPromise" v-model="transaction.manualReason" field="manualReason" />
      </FormRow>
      <FormRow label="Notes">
        <FormInputText placeholder="Transaction Note" v-model="transaction.note" field="note" />
      </FormRow>

      <template v-if="transaction.stockId != undefined">
        <FormHorizontalRule />
        <h3>Incoming / Outgoing List</h3>

        <StaticDataTable class="static-table-list" variant="table-sm">
          <template v-slot:header>
            <tr>
              <th width="10%">Type</th>
              <th width="25%" v-if="stockTypeIsGlass">Glass Size</th>
              <th width="10%" v-if="stockTypeIsGlass">Height</th>
              <th width="10%" v-if="stockTypeIsGlass">Width</th>
              <th width="8%" v-if="stockTypeIsGlass">Location</th>
              <th width="7%" v-if="stockTypeIsGlass"></th>
              <th width="10%">Quantity</th>
              <th width="10%" v-html="uomDisplayUnits"></th>
              <th width="10%">Actions</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="(listItem, index) in transaction.transactionList" :key="index">
              <td>
                <span class="label label-stock-transaction-out" v-if="listItem.direction == 'out'">Out</span>
                <span class="label label-stock-transaction-in" v-if="listItem.direction == 'in'">In</span>
              </td>
              <td v-if="stockTypeIsGlass">
                <FormSelect :options="glassSizeListOut" :value="listItem.glassLevelId" @input="glassSizeUpdated(listItem, $event)" v-if="listItem.direction == 'out'" />
                <FormSelect :options="glassSizeListIn" :value="listItem.glassLevelId" @input="glassSizeUpdated(listItem, $event)" v-if="listItem.direction == 'in'" />
              </td>
              <td v-if="stockTypeIsGlass">
                <FormInputText type="number" v-model="listItem.height" v-if="listItem.direction == 'in' && listItem.isOffcut" />
                <span v-if="listItem.direction == 'out' || !listItem.isOffcut">{{listItem.height}}</span>
              </td>
              <td v-if="stockTypeIsGlass">
                <FormInputText type="number" v-model="listItem.width" v-if="listItem.direction == 'in' && listItem.isOffcut" />
                <span v-if="listItem.direction == 'out' || !listItem.isOffcut">{{listItem.width}}</span>
              </td>
              <td v-if="stockTypeIsGlass">
                <FormInputText placeholder="Rack" v-model="listItem.offcutRack" v-if="listItem.direction == 'in' && listItem.isOffcut" />
                <span v-if="listItem.direction == 'out' || !listItem.isOffcut">-</span>
              </td>
              <td v-if="stockTypeIsGlass">
                <FormInputText placeholder="Row" v-model="listItem.offcutRow" v-if="listItem.direction == 'in' && listItem.isOffcut" />
              </td>
              <td>
                <FormInputText type="number" placeholder="Quantity" v-model="listItem.quantity" v-show="!listItem.isOffcut" />
                <span v-if="listItem.isOffcut">1</span>
              </td>
              <td :class="[listItem.direction == 'out' ? 'stock-transaction-out' : 'stock-transaction-in']">
                {{listItem.direction == 'out' ? '-' : '+'}}{{getListItemAmount(listItem)}}<span v-html="uomDisplayUnits"></span>
              </td>
              <td>
                <DropdownMenu buttonClass="btn-white btn-xs" title="Action">
                  <DropdownMenuItem @click="removeRow(index)" icon="fa-trash-alt">Remove</DropdownMenuItem>
                </DropdownMenu>
              </td>
            </tr>
            <tr class="table-totals-row-header">
              <td></td>
              <td colspan="5" v-if="stockTypeIsGlass"></td>
              <td class="text-right font-weight-bold">Net:</td>
              <td :class="['font-weight-bold', getNetAdjustAmount() >= 0 ? 'stock-transaction-in' : 'stock-transaction-out']">{{getNetAdjustAmount() >= 0 ? '+' : ''}}{{getNetAdjustAmount()}}<span v-html="uomDisplayUnits"></span></td>
              <td></td>
            </tr>
          </template>
        </StaticDataTable>
        <ActionButton type="new" class="btn-xs mr-2" @click="addIncoming"> Add Incoming</ActionButton>
        <ActionButton type="new" class="btn-xs" @click="addOutgoing"> Add Outgoing</ActionButton>
      </template>

    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import Utils from '@/utils/utils'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'
import StaticDataTable from '@/components/table/staticDataTable.vue'
import { DescriptorLookupTypes, InventoryStockTypes, StockTransactionDirectionTypes, StockTransactionTypes, StockTransactionTypeLookup, UOMTypeUnitsLookup, UOMTypes } from '@/store/types'

export default {
  name: "StockTransactionDialog",
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    StaticDataTable
  },
  props: {
  },
  data() {
    return {
      id: null,
      transaction: {
       stockId: null,
        transactionList: []
      },
      dialogForm: {},
      submitting: false,
      stockList: [],
      glassLevelList: [],
      glassSizeListIn: [],
      glassSizeListOut: [],
      stockType: null,
      uomType: null,
      
      adjustReasonListPromise: this.getAdjustReasonListPromise(),
    }
  },
  watch: {
    'transaction.stockId': {
      deep: true,
      handler: function(newVal, oldVal) {
        this.$data.glassSizeListIn = []
        this.$data.glassSizeListOut = []
        this.$data.glassLevelList = []

        // Clear transactions if stock changed
        if(newVal != oldVal)
          this.transaction.transactionList = []

        if(this.transaction.stockId == null) {
          return
        }          
        // Update glass size/level list
        this.$apiService.inventoryService.getStock(this.transaction.stockId)
        .then(response => {
          this.stockType = response.data.type
          this.uomType = response.data.uomType

          // Incoming List
          if(_.isArray(response.data.glassSizeList)) {
            this.glassLevelList = response.data.glassSizeList.reduce((list, glassSize) => {
              return _.concat(list, glassSize.glassLevels)
            }, [])
            this.glassLevelList = _.concat(this.glassLevelList, response.data.glassOffcutList)

            // Build size/level list
            this.glassSizeListIn = response.data.glassSizeList.reduce((list, glassSize) => {
              if(!glassSize.isActive || glassSize.glassLevels.length == 0)
                return list
              list.push({
                name: `${glassSize.height}x${glassSize.width}`,
                children: glassSize.glassLevels.filter(glassLevel => !glassLevel.isOffcut).map(glassLevel => {
                  return { value: glassLevel.id, name: `${glassLevel.height}x${glassLevel.width}` }
                })
              })
              return list
            }, [])
          }

          // Incoming - New Offcut
          this.glassSizeListIn.push({
            name: 'Offcuts',
            children: [{ value: 'offcut', name: 'New Offcut' }]
          })

          // Outgoing List
          if(_.isArray(response.data.glassSizeList)) {
            this.glassSizeListOut = response.data.glassSizeList.reduce((list, glassSize) => {
              if(!glassSize.isActive || glassSize.glassLevels.length == 0)
                return list
              list.push({
                name: `${glassSize.height}x${glassSize.width}`,
                children: glassSize.glassLevels.filter(glassLevel => !glassLevel.isOffcut).map(glassLevel => {
                  return { value: glassLevel.id, name: `${glassLevel.height}x${glassLevel.width}` }
                })
              })
              return list
            }, [])

            // Outgoing Offcuts
            if(response.data.glassOffcutList.length > 0) {
              this.glassSizeListOut = _.concat(this.glassSizeListOut, {
                name: 'Offcuts',
                children: response.data.glassOffcutList.map(offcut => {
                  return { value: offcut.id, name: `${offcut.height}x${offcut.width}` }
                })
              })
            }
          }
        })
      }
    }
  },
  mounted() {
    // Download stock list that is glass or trackable general stock
    this.$apiService.inventoryService.getStockList().then(response => {
      this.$data.stockList = response.data
        .filter(stock => stock.isActive && (stock.type == InventoryStockTypes.glass || (stock.type == InventoryStockTypes.general && stock.isTrackingQuantity)))
        .map(stock => {
          return {
            value: stock.id,
            name: `${stock.code} | ${stock.name}`
          }
        })
    })

    // Get stock adjust reason lookup
    this.$apiService.adminService.getDescriptorLookupListForType(DescriptorLookupTypes.stockAdjustReason)
    .then(response => {
      this.$data.stockAdjustReasonLookup = response.data.map(lookup => {
        return {
          value: lookup.description,
          name: lookup.description
        }
      })
    })
  },
  computed: {
    stockTypeIsGlass() { return this.stockType == InventoryStockTypes.glass },

    submitDisabled() {
      if(this.transaction.stockId == null)
        return true
      if(this.$data.transaction.transactionList.length == 0)
        return true
      return false
    },
    uomDisplayUnits() {
      if(this.$data.uomType == null)
        return ''
      return Utils.getLookupName(UOMTypeUnitsLookup, this.$data.uomType)
    }
  },
  methods: {
    show() {
      // Reset form
      this.transaction = {
       stockId: null,
        transactionList: [],
        manualReason: '',
        note: ''
      }
      this.dialogForm = {}
      this.submitting = false

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true

      var updateData = _.cloneDeep(this.$data.transaction)
      updateData = _.pick(updateData, ['stockId', 'manualReason', 'note'])

      updateData.transactionList = this.$data.transaction.transactionList.map(listItem => {
        return {
          direction: listItem.direction,
         glassLevelId: listItem.isOffcut ? null : parseInt(listItem.glassLevelId),
         glassSizeId: parseInt(listItem.glassSizeId),
          isOffcut: listItem.isOffcut,
          isOffcutAvailable: listItem.isOffcutAvailable,
          offcutRack: listItem.offcutRack,
          offcutRow: listItem.offcutRow,
          width: listItem.width,
          height: listItem.height,
          quantity: listItem.isOffcut ? 1 : listItem.quantity
        }
      })

      this.dialogForm = this.clearFormResponse()

      this.$apiService.inventoryService.createStockAdjustment(updateData)
      .then(() => {
        this.submitting = false
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.submitting = false
        this.$data.dialogForm = this.processFormResponse(err)
      })
    },
    addIncoming() {
      this.$data.transaction.transactionList.push({
        direction: StockTransactionDirectionTypes.in,
       glassLevelId: null,
       glassSizeId: null,
        isOffcut: false,
        width: 0,
        height: 0,
        quantity: 0
      })
    },
    addOutgoing() {
      this.$data.transaction.transactionList.push({
        direction: StockTransactionDirectionTypes.out,
       glassLevelId: null,
       glassSizeId: null,
        isOffcut: false,
        width: 0,
        height: 0,
        quantity: 0
      })
    },
    removeRow(index) {
      this.$data.transaction.transactionList.splice(index, 1)
    },
    glassSizeUpdated(listItem, sizeId) {
      listItem.glassLevelId = sizeId
      listItem.isOffcut = (sizeId == 'offcut')
      listItem.isOffcutAvailable = true
      listItem.width = 0
      listItem.height = 0

      if(!listItem.isOffcut) {
        var levelItem = this.glassLevelList.find(level => level.id == listItem.glassLevelId)
        if(levelItem != null) {
          listItem.glassSizeId = levelItem.glassSizeId
          listItem.width = levelItem.width
          listItem.height = levelItem.height
        }
      }      
    },
    getListItemAmount(listItem) {
      if(this.uomType == UOMTypes.metresq)
        return Utils.roundQuantityValue(Utils.convertMillimetreSqToMetreSq(listItem.width*listItem.height*listItem.quantity)).toFixed(3)
      else
        return Utils.roundQuantityValue(listItem.quantity).toFixed(3)
    },
    getNetAdjustAmount() {
      if(this.uomType == UOMTypes.metresq) {
        // Square Metres
        return Utils.roundQuantityValue(this.$data.transaction.transactionList.reduce((curAdjust, listItem) => {
          return curAdjust + Utils.roundQuantityValue(Utils.convertMillimetreSqToMetreSq(listItem.width*listItem.height*listItem.quantity)) * (listItem.direction == StockTransactionDirectionTypes.out ? -1 : 1)
        }, 0)).toFixed(3)
      } else {
        // Other quantity
        return Utils.roundQuantityValue(this.$data.transaction.transactionList.reduce((curAdjust, listItem) => {
          return curAdjust + Utils.roundQuantityValue(listItem.quantity) * (listItem.direction == StockTransactionDirectionTypes.out ? -1 : 1)
        }, 0)).toFixed(3)
      }
    },

    getAdjustReasonListPromise() {
      return new Promise((resolve, reject) => {
        return this.$apiService.adminService.getDescriptorLookupListForType(DescriptorLookupTypes.stockAdjustReason)
        .then(response => {
          resolve(response.data.map(lookup => {
            return { value: lookup.description, name: lookup.description }
          }))
        })
        .catch(() => { reject() })
      })
    }
  }
}
</script>